/**
 * Agent Test Chat Component
 * 
 * Embedded chat interface for testing agents from the Admin Console.
 * Supports message streaming, tool calls display, and emergency stop.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Loader2, StopCircle, Bot, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { rawApi } from '@/lib/api-client';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'tool';
  content: string;
  toolCalls?: Array<{
    name: string;
    arguments: Record<string, unknown>;
  }>;
  toolResults?: Array<{
    name: string;
    success: boolean;
    output: string;
  }>;
  timestamp: Date;
}

interface AgentTestChatProps {
  agentId: string;
  agentName: string;
}

export function AgentTestChat({ agentId, agentName }: AgentTestChatProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Create conversation on first message
  const createConversation = useCallback(async (): Promise<string> => {
    const response = await rawApi.post<{ data: { id: string } }>('/conversations', {
      agent_id: agentId,
    });
    const convId = response.data.data.id;
    setConversationId(convId);
    return convId;
  }, [agentId]);

  // Send message to the agent
  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: input,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      let convId = conversationId;
      if (!convId) {
        convId = await createConversation();
      }

      // Add user message to conversation
      await rawApi.post(`/conversations/${convId}/messages`, {
        role: 'user',
        content: userMessage.content,
      });

      // TODO (Sprint 8 Stream A): Integrate with real agent streaming endpoint
      // Production implementation:
      // - Use SSE: POST /api/v1/agents/{agentId}/chat with EventSource
      // - Handle streaming events: content, tool_calls, tool_results, done, error
      // - Replace simulated response below with real streaming handler
      //
      // For now, simulate a placeholder response to demonstrate the UI:
      const assistantMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: `[Test Mode] Agent "${agentName}" received your message. Full agent integration with streaming responses and tool execution will be available after E2E integration is complete.`,
        timestamp: new Date(),
      };

      // Add assistant message
      await rawApi.post(`/conversations/${convId}/messages`, {
        role: 'assistant',
        content: assistantMessage.content,
      });

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: `Error: ${error instanceof Error ? error.message : 'Failed to send message'}`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStop = async () => {
    // TODO: Integrate with /api/v1/tools/emergency-stop endpoint
    // Production implementation:
    // await rawApi.post('/tools/emergency-stop', { agent_id: agentId });
    // This will cancel any running tool executions and stop the streaming response
    //
    // For now, just reset the loading state:
    setIsLoading(false);
  };

  return (
    <div className="flex flex-col h-[500px] border rounded-lg">
      {/* Header */}
      <div className="flex items-center gap-2 p-3 border-b bg-muted/50">
        <Bot className="h-5 w-5 text-primary" />
        <span className="font-medium">Testing: {agentName}</span>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-4">
          {messages.length === 0 && (
            <div className="text-center text-muted-foreground py-8">
              Start a conversation to test the agent
            </div>
          )}

          {messages.map((message) => (
            <div
              key={message.id}
              className={cn(
                'flex gap-3',
                message.role === 'user' ? 'justify-end' : 'justify-start'
              )}
            >
              {message.role !== 'user' && (
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Bot className="h-4 w-4 text-primary" />
                </div>
              )}

              <div
                className={cn(
                  'max-w-[80%] rounded-lg px-4 py-2',
                  message.role === 'user'
                    ? 'bg-primary text-primary-foreground'
                    : message.role === 'tool'
                    ? 'bg-yellow-100 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700'
                    : 'bg-muted'
                )}
              >
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>

                {message.toolCalls && (
                  <div className="mt-2 text-xs opacity-70">
                    {message.toolCalls.map((call, i) => (
                      <div key={i}>🔧 {call.name}</div>
                    ))}
                  </div>
                )}

                {message.toolResults && (
                  <div className="mt-2 text-xs">
                    {message.toolResults.map((result, i) => (
                      <div
                        key={i}
                        className={result.success ? 'text-green-600' : 'text-red-600'}
                      >
                        {result.success ? '✓' : '✗'} {result.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {message.role === 'user' && (
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <User className="h-4 w-4 text-primary-foreground" />
                </div>
              )}
            </div>
          ))}

          {isLoading && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Bot className="h-4 w-4 text-primary" />
              </div>
              <div className="bg-muted rounded-lg px-4 py-2">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            </div>
          )}

          <div ref={scrollRef} />
        </div>
      </div>

      {/* Input */}
      <div className="p-3 border-t">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSend();
          }}
          className="flex gap-2"
        >
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type a message..."
            disabled={isLoading}
            className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          />

          {isLoading ? (
            <Button type="button" variant="destructive" onClick={handleStop} size="icon">
              <StopCircle className="h-4 w-4" />
            </Button>
          ) : (
            <Button type="submit" disabled={!input.trim()} size="icon">
              <Send className="h-4 w-4" />
            </Button>
          )}
        </form>
      </div>
    </div>
  );
}
